import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Trans, useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useRoute } from '@react-navigation/native';
import * as Linking from 'expo-linking';

import {
  Form,
  Field,
  FormButton,
  Separator,
  CheckBoxField,
  AuthFormLayout,
  AuthLayoutChildContainerRefType,
  BaseTextLink,
  BaseText,
  FormPickerField,
  TextInputRefType,
} from '@components';
import { SignUpSchemaDataType, signUpSchema } from '@validation';
import { CountryCodeEnum, synthoURLs } from '@constants';
import { AuthNavigatorRoutePropType } from '@navigation';
import { IS_WEB } from '@utils';
import { PasswordValidation } from './PasswordValidation';

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-start',
});

const NameFieldContainer = styled(View)({
  flex: 1,
});

const Text = styled(BaseText)({
  flexShrink: 1,
  color: '$heading800',
  variant: 'text.small',
  ml: '$5',
});

const TextLink = styled(BaseTextLink)({
  variant: 'text.small',
});

export interface ISignUpFormProps {
  onSubmit: (
    values: SignUpSchemaDataType,
    formik: FormikHelpers<SignUpSchemaDataType>,
  ) => void;
}

type RoutePropType = AuthNavigatorRoutePropType<'SignUp'>;

export const SignUpForm = observer<ISignUpFormProps>(
  ({ onSubmit }) => {
    const { t, i18n } = useTranslation();
    const layoutRef = useRef<AuthLayoutChildContainerRefType>(null);
    const route = useRoute<RoutePropType>();
    const emailAgainInputRef = useRef<TextInputRefType>(null);

    useEffect(() => {
      if (IS_WEB) {
        // Prevent copy/paste value from/into "email again" input
        const ref =
          emailAgainInputRef.current as unknown as HTMLInputElement;
        ref.addEventListener('paste', (e) => e.preventDefault());
        ref.addEventListener('copy', (e) => e.preventDefault());
      }
    }, []);

    const initialValues: SignUpSchemaDataType = {
      email: route.params?.email ?? '',
      emailAgain: '',
      password: '',
      // passwordAgain: '',
      firstName: '',
      lastName: '',
      country: '',
      agreement: false,
    };

    const countryOptions = useMemo(
      () =>
        Object.values(CountryCodeEnum).map((c) => ({
          title: t(`countries.${c}`),
          value: c,
        })),
      [i18n.language],
    );

    function navigateToTerms() {
      if (IS_WEB) {
        window.open(synthoURLs.TERMS_AND_CONDITIONS, '_blank');
      } else {
        Linking.openURL(synthoURLs.TERMS_AND_CONDITIONS);
      }
    }

    function navigateToPrivacy() {
      if (IS_WEB) {
        window.open(synthoURLs.PRIVACY_POLICY, '_blank');
      } else {
        Linking.openURL(synthoURLs.PRIVACY_POLICY);
      }
    }

    return (
      <AuthFormLayout
        childrenContainerRef={layoutRef}
        topText={t('signUp.stepOf', { step: 1, ofSteps: 3 })}
        title={t('signUp.title')}
        scrollEnabled
        isUseVerticalExtraSpace
      >
        <Form<SignUpSchemaDataType>
          initialValues={initialValues}
          validationSchema={signUpSchema}
          onSubmit={onSubmit}
        >
          <Field
            name="email"
            placeholder={t('signUp.email')}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <Separator />
          <Field
            inputRef={emailAgainInputRef}
            name="emailAgain"
            placeholder={t('signUp.confirmEmail')}
            autoCapitalize="none"
            keyboardType="email-address"
          />
          <Separator />
          <Field
            name="password"
            placeholder={t('signUp.password')}
            secureTextEntry
            autoCapitalize="none"
          />
          <Separator />
          {/* <Field
            name="passwordAgain"
            placeholder={t('signUp.confirmPassword')}
            secureTextEntry
            autoCapitalize="none"
          />
          <Separator /> */}
          <PasswordValidation />
          <Separator />
          <RowContainer>
            <NameFieldContainer>
              <Field
                name="firstName"
                placeholder={t('signUp.firstName')}
              />
            </NameFieldContainer>
            <Separator vertical />
            <NameFieldContainer>
              <Field
                name="lastName"
                placeholder={t('signUp.lastName')}
              />
            </NameFieldContainer>
          </RowContainer>

          <Separator />
          <FormPickerField
            name="country"
            containerRef={layoutRef}
            placeholder={t('signUp.country')}
            options={countryOptions}
          />

          <Separator />
          <RowContainer>
            <CheckBoxField name="agreement" />
            <Text>
              <Trans
                i18nKey="signUp.agreement"
                components={{
                  termsLink: <TextLink onPress={navigateToTerms} />,
                  privacyLink: (
                    <TextLink onPress={navigateToPrivacy} />
                  ),
                }}
              />
            </Text>
          </RowContainer>

          <Separator />
          <FormButton>{t('signUp.next')}</FormButton>
        </Form>
      </AuthFormLayout>
    );
  },
);
